import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ShipmentService } from './shipment-service';
import { DbService } from './db.service';
import { ToastService } from './toast.service';
import { IShipmentHistory } from '../interfaces/IShipmentHistory';

const BACKGROUND_SYNC_TAG = 'shipment-updates';

@Injectable({
  providedIn: 'root',
})
export class BackgroundSyncService {
  private syncInProgress: Promise<void> | null = null;

  constructor(
    private shipmentService: ShipmentService,
    private dbService: DbService,
    private toast: ToastService,
  ) {
    this.init();
  }

  private async init(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    this.subscribeOnlineOffline();
    await this.notifyWorkerConfig();

    if (navigator.onLine) {
      await this.requestPendingShipmentSync();
    }
  }

  private subscribeOnlineOffline(): void {
    window.addEventListener('online', () => {
      this.toast.info('Connection restored. Sending pending updates.');
      void this.requestPendingShipmentSync();
    });

    window.addEventListener('offline', () => {
      this.toast.info('There is no connection. Changes will be saved locally.');
    });
  }

  private async notifyWorkerConfig(): Promise<void> {
    const registration = await this.getServiceWorkerRegistration();
    const apiBase = (window as any).configuration?.enviroment?.MiAplicacionApi;

    if (registration?.active) {
      registration.active.postMessage({
        type: 'SET_CONFIG',
        apiBase,
      });
    }
  }

  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      return (await navigator.serviceWorker.getRegistration()) ?? null;
    } catch {
      return null;
    }
  }

  private async requestPendingShipmentSync(): Promise<void> {
    await this.notifyWorkerConfig();

    const registration = await this.getServiceWorkerRegistration();
    if (registration?.active) {
      const swRegistration = registration as ServiceWorkerRegistration & {
        sync?: { register(tag: string): Promise<void> };
      };

      if ('SyncManager' in window && swRegistration.sync) {
        try {
          await swRegistration.sync.register(BACKGROUND_SYNC_TAG);
          return;
        } catch (err) {
          console.warn('Background sync registration failed:', err);
        }
      }

      registration.active.postMessage({
        type: 'SYNC_PENDING',
      });
      return;
    }

    await this.syncPendingShipmentUpdates();
  }

  public async syncPendingShipmentUpdates(): Promise<void> {
    if (this.syncInProgress) {
      return this.syncInProgress;
    }

    this.syncInProgress = this.runPendingShipmentSync();

    try {
      await this.syncInProgress;
    } finally {
      this.syncInProgress = null;
    }
  }

  private async runPendingShipmentSync(): Promise<void> {
    let pending = await this.dbService.getShipmentUpdates();
    pending = pending.sort((a, b) => a.queuedId - b.queuedId );

    if (!pending || pending.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      pending.map(item => this.sendShipmentHistory(item.queuedId, item.data)),
    );

    const failed = results.filter(result => result.status === 'rejected');
    if (failed.length === 0) {
      this.toast.success('Pending updates successfully sent.');
    }
  }

  private async sendShipmentHistory(queuedId: number, history: IShipmentHistory): Promise<void> {
    return firstValueFrom(this.shipmentService.saveShipmentHistory(history)).then(result => {
      if (result.success) {
        return this.dbService.removeShipmentUpdate(queuedId);
      }

      return Promise.reject(new Error('The update could not be sent')); 
    });
  }
}
